import axios from 'axios';
import { getAuthToken, removeAuthToken, secureStorage } from '../utils/securityUtils';

// ============================================
// API Configuration
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

console.log('API: Initializing with base URL:', API_BASE_URL);
console.log('API: Full URL will be:', API_BASE_URL);

// ============================================
// Utility Functions
// ============================================

/**
 * Extract data from various API response formats for LIST endpoints
 * Handles Laravel pagination, nested data structures, and direct arrays
 */
const extractDataFromResponse = (response) => {
  if (!response || !response.data) {
    return [];
  }
  
  // Handle direct array
  if (Array.isArray(response.data)) {
    return response.data;
  }
  
  // Handle Laravel API response format with success flag
  if (response.data.success !== undefined && response.data.data) {
    console.log('🔍 extractDataFromResponse: Laravel API format detected');
    // Check for paginated data
    if (response.data.data.data && Array.isArray(response.data.data.data)) {
      console.log('✅ extractDataFromResponse: Paginated data found, length:', response.data.data.data.length);
      return response.data.data.data; // Laravel pagination
    }
    // Check for direct data array
    if (Array.isArray(response.data.data)) {
      console.log('✅ extractDataFromResponse: Direct data array found, length:', response.data.data.length);
      return response.data.data;
    }
    // Return the data object itself if not an array
    if (typeof response.data.data === 'object') {
      console.log('✅ extractDataFromResponse: Data object found, keys:', Object.keys(response.data.data));
      return response.data.data;
    }
  }
  
  // Handle nested data property without success flag
  if (response.data.data) {
    console.log('🔍 extractDataFromResponse: Nested data property found');
    if (Array.isArray(response.data.data)) {
      console.log('✅ extractDataFromResponse: Nested array found, length:', response.data.data.length);
      return response.data.data;
    }
    if (response.data.data.data && Array.isArray(response.data.data.data)) {
      console.log('✅ extractDataFromResponse: Double nested array found, length:', response.data.data.data.length);
      return response.data.data.data;
    }
    // Return the nested data object
    console.log('✅ extractDataFromResponse: Nested object found, keys:', Object.keys(response.data.data));
    return response.data.data;
  }
  
  // Handle direct Laravel pagination response (like tire-products)
  if (response.data.data && Array.isArray(response.data.data) && response.data.current_page) {
    console.log('✅ extractDataFromResponse: Direct Laravel pagination found, length:', response.data.data.length);
    return response.data.data;
  }
  
  // If response.data is an object with an id (single resource), return it
  if (response.data.id) {
    console.log('✅ extractDataFromResponse: Single resource found, id:', response.data.id);
    return response.data;
  }
  
  // Return empty array as fallback for lists
  console.log('❌ extractDataFromResponse: No data found, returning empty array');
  return [];
};

/**
 * Extract data from API response for SINGLE resource endpoints (show, create, update)
 * Returns the single object, not an array
 */
const extractSingleDataFromResponse = (response) => {
  console.log('🔍 extractSingleDataFromResponse: Processing response:', {
    hasResponse: !!response,
    hasData: !!response?.data,
    dataType: typeof response?.data,
    isArray: Array.isArray(response?.data),
    dataKeys: response?.data ? Object.keys(response.data) : [],
    success: response?.data?.success,
    hasNestedData: !!response?.data?.data
  });

  if (!response || !response.data) {
    console.log('❌ extractSingleDataFromResponse: No response or data, returning null');
    return null;
  }
  
  // Handle Laravel API response format with success flag
  if (response.data.success !== undefined) {
    console.log('🔍 extractSingleDataFromResponse: Laravel API format detected, success:', response.data.success);
    
    // For single resource
    if (response.data.data && !Array.isArray(response.data.data)) {
      console.log('✅ extractSingleDataFromResponse: Single resource found, keys:', Object.keys(response.data.data));
      return response.data.data;
    }
    
    // Return the data object itself if not an array
    if (response.data.data && typeof response.data.data === 'object') {
      console.log('✅ extractSingleDataFromResponse: Data object found, keys:', Object.keys(response.data.data));
      return response.data.data;
    }
  }
  
  // Handle nested data property without success flag
  if (response.data.data && !Array.isArray(response.data.data)) {
    console.log('✅ extractSingleDataFromResponse: Nested object found, keys:', Object.keys(response.data.data));
    return response.data.data;
  }
  
  // If response.data is an object with an id (single resource), return it
  if (response.data.id) {
    console.log('✅ extractSingleDataFromResponse: Single resource found, id:', response.data.id);
    return response.data;
  }
  
  // Return null as fallback for single resources
  console.log('❌ extractSingleDataFromResponse: No single resource found, returning null');
  return null;
};

/**
 * Extract pagination info from Laravel paginated responses
 */
const extractPaginationInfo = (response) => {
  if (!response || !response.data) return null;
  
  // Check for Laravel pagination structure
  if (response.data.data && response.data.current_page) {
    return {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
      from: response.data.from,
      to: response.data.to
    };
  }
  
  if (response.data.success && response.data.data) {
    const data = response.data.data;
    if (data.current_page) {
      return {
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
        from: data.from,
        to: data.to
      };
    }
  }
  
  return null;
};

// ============================================
// Request Interceptor
// ============================================
api.interceptors.request.use((config) => {
  // Use secureStorage for token (matches AuthContext)
  const token = getAuthToken();
  // Check both secureStorage and localStorage for backward compatibility
  const activeTenantId = secureStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId');
  const user = secureStorage.getItem('user') || JSON.parse(localStorage.getItem('user') || '{}');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Handle tenant context
  if (user.super_admin) {
    if (activeTenantId) {
      config.headers['X-Active-Tenant-ID'] = activeTenantId;
      console.log('🔍 API: Super admin with active tenant:', activeTenantId);
    } else {
      // Only warn for non-exempt endpoints
      const exemptPaths = ['/auth', '/super-admin/tenants', '/super-admin/system'];
      const isExempt = exemptPaths.some(path => config.url?.includes(path));
      
      if (!isExempt) {
        console.warn('⚠️ API: Super admin without active tenant context for:', config.url);
      }
    }
  } else if (user.tenant_id) {
    config.headers['X-Tenant-ID'] = user.tenant_id;
    console.log('🔍 API: Regular user with tenant:', user.tenant_id);
  } else {
    // Check if tenant_id is stored separately in localStorage
    const storedTenantId = localStorage.getItem('tenant_id');
    if (storedTenantId) {
      config.headers['X-Tenant-ID'] = storedTenantId;
      console.log('🔍 API: Using stored tenant_id:', storedTenantId);
    } else {
      console.warn('⚠️ API: No tenant context available for request:', config.url);
    }
  }

  // If data is FormData, remove Content-Type header to let axios set it automatically with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }



  return config;
}, (error) => {
  return Promise.reject(error);
});

// ============================================
// Response Interceptor
// ============================================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('🔍 API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/auth');
      const isLoginPage = window.location.pathname === '/login';
      
      if (!isAuthEndpoint && !isLoginPage) {
        console.log('🔍 API: Unauthorized - clearing auth data and redirecting to login');
        
        // Clear secureStorage (primary storage)
        removeAuthToken();
        secureStorage.removeItem('user');
        secureStorage.removeItem('tenant');
        secureStorage.removeItem('tenant_id');
        secureStorage.removeItem('activeTenantId');
        secureStorage.removeItem('is_super_admin');
        
        // Also clear localStorage for backward compatibility
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('activeTenantId');
        localStorage.removeItem('is_super_admin');
        
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message;
      
      if (errorMessage === 'Tenant is not active') {
        console.log('🔍 API: Tenant not active - clearing auth data');
        
        // Clear secureStorage (primary storage)
        removeAuthToken();
        secureStorage.removeItem('user');
        secureStorage.removeItem('tenant');
        secureStorage.removeItem('tenant_id');
        secureStorage.removeItem('activeTenantId');
        
        // Also clear localStorage for backward compatibility
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('activeTenantId');
        
        alert('Your tenant account is not active. Please contact your administrator.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        
      } else if (errorMessage === 'No active tenant context. Please switch to a tenant first.') {
        console.warn('⚠️ API: Super admin needs to select tenant context');
        const user = secureStorage.getItem('user') || JSON.parse(localStorage.getItem('user') || '{}');
        const activeTenantId = secureStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId');
        if (user.super_admin && !activeTenantId) {
          console.log('Super admin: Please select a tenant to access this resource');
        }
      } else {
        console.warn('⚠️ API: Access forbidden:', errorMessage);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// API Service Methods
// ============================================

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getUser: () => api.get('/auth/user'),
  refresh: () => api.post('/auth/refresh'),
  validate: () => api.get('/auth/user'), // Use getUser endpoint for validation
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data)
};

// Tenant API
export const tenantAPI = {
  getCurrent: () => authAPI.getUser(),
  update: (data) => authAPI.updateProfile(data),
  getSettings: () => api.get('/tenant/settings'),
  updateSettings: (data) => api.put('/tenant/settings', data),
  getFeatures: () => api.get('/tenant/features'),
  getUsage: () => api.get('/tenant/usage'),
  getBilling: () => api.get('/tenant/billing'),
  upgradePlan: (plan) => api.post('/tenant/upgrade', { plan }),
  cancelSubscription: () => api.post('/tenant/cancel-subscription')
};

// Tire Brands API
export const tireBrandsAPI = {
  getAll: async (params) => {
    const response = await api.get('/tire-brands', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/tire-brands', data),
  update: (id, data) => {
    // Laravel doesn't parse FormData text fields with PUT requests
    // Use POST with method spoofing for FormData
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/tire-brands/${id}`, data);
    }
    // For regular JSON data, use PUT
    return api.put(`/tire-brands/${id}`, data);
  },
  delete: (id) => api.delete(`/tire-brands/${id}`),
  show: async (id) => {
    const response = await api.get(`/tire-brands/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Tire Types API
export const tireTypesAPI = {
  getAll: async (params) => {
    const response = await api.get('/tire-types', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/tire-types', data),
  update: (id, data) => api.put(`/tire-types/${id}`, data),
  delete: (id) => api.delete(`/tire-types/${id}`),
  show: async (id) => {
    const response = await api.get(`/tire-types/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Battery Types API
export const batteryTypesAPI = {
  getAll: async (params) => {
    const response = await api.get('/battery-types', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/battery-types', data),
  update: (id, data) => api.put(`/battery-types/${id}`, data),
  delete: (id) => api.delete(`/battery-types/${id}`),
  show: async (id) => {
    const response = await api.get(`/battery-types/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Tags API
export const tagsAPI = {
  getAll: async (params) => {
    const response = await api.get('/tags', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  show: async (id) => {
    const response = await api.get(`/tags/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Patterns API
export const patternsAPI = {
  getAll: async (params) => {
    const response = await api.get('/patterns', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => {
    // If data is FormData, axios will automatically set Content-Type
    // Otherwise, use JSON
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return api.post('/patterns', data, config);
  },
  update: (id, data) => {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return api.put(`/patterns/${id}`, data, config);
  },
  delete: (id) => api.delete(`/patterns/${id}`),
  show: async (id) => {
    const response = await api.get(`/patterns/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  },
  getByBrand: async (brandId) => {
    console.log('🔍 patternsAPI.getByBrand called with brandId:', brandId, typeof brandId);
    // Fixed: Laravel route is /patterns/brand/{brandId}, not /patterns/by-brand/{brandId}
    const url = `/patterns/brand/${brandId}`;
    console.log('🔍 Making request to:', url);
    const response = await api.get(url);
    console.log('🔍 patternsAPI.getByBrand raw response:', response);
    console.log('🔍 patternsAPI.getByBrand response.data:', response.data);
    const extractedData = extractDataFromResponse(response);
    console.log('🔍 patternsAPI.getByBrand extracted data:', extractedData);
    return { ...response, data: extractedData };
  }
};

export const tiresAPI = {
  getAll: async (params = {}) => {
    console.log('🔍 tiresAPI.getAll params:', params);
    try {
      const response = await api.get('/tire-products', { params });
      console.log('✅ Raw response:', response);
      
      let tiresList = [];
      let pagination = null;
      
      // Handle the Laravel API response structure
      if (response.data) {
        // If success wrapper exists
        if (response.data.success !== undefined && response.data.data) {
          const data = response.data.data;
          // Check for paginated data
          if (data.data && Array.isArray(data.data)) {
            tiresList = data.data;
            pagination = {
              current_page: data.current_page,
              last_page: data.last_page,
              per_page: data.per_page,
              total: data.total,
              from: data.from,
              to: data.to
            };
          } else if (Array.isArray(data)) {
            tiresList = data;
          }
        }
        // Direct pagination structure
        else if (response.data.data && Array.isArray(response.data.data)) {
          tiresList = response.data.data;
          pagination = {
            current_page: response.data.current_page,
            last_page: response.data.last_page,
            per_page: response.data.per_page,
            total: response.data.total,
            from: response.data.from,
            to: response.data.to
          };
        }
        // Direct array
        else if (Array.isArray(response.data)) {
          tiresList = response.data;
        }
      }
      
      console.log('✅ Extracted tires:', tiresList.length, 'Pagination:', pagination);
      return { 
        data: tiresList, 
        pagination,
        raw: response.data // Keep raw response for debugging
      };
    } catch (error) {
      console.error('❌ tiresAPI.getAll error:', error);
      throw error;
    }
  },
  
  show: (id) => api.get(`/tire-products/${id}`),  // ✅ Fixed backticks
  
  create: async (data) => {
    console.log('🔍 tiresAPI.create - Creating tire with data');
    console.log('🔍 Data type:', data instanceof FormData ? 'FormData' : typeof data);
    
    // FormData will be handled automatically by the request interceptor
    const response = await api.post('/tire-products', data);
    console.log('🔍 tiresAPI.create - Tire creation response:', response);
    
    return response;
  },
  
  update: async (id, data) => {
    console.log('🔍 tiresAPI.update - Updating tire with data');
    console.log('🔍 Data type:', data instanceof FormData ? 'FormData' : typeof data);
    
    // Use method spoofing for FormData (Laravel doesn't parse FormData fields on PUT)
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      const response = await api.post(`/tire-products/${id}`, data);
      console.log('🔍 tiresAPI.update - Tire update response (POST with _method=PUT):', response);
      return response;
    }
    
    // Regular PUT for non-FormData
    const response = await api.put(`/tire-products/${id}`, data);
    console.log('🔍 tiresAPI.update - Tire update response:', response);
    
    return response;
  },
  
  delete: (id) => api.delete(`/tire-products/${id}`),

  getByGroup: async (params = {}) => {
    try {
      const response = await api.get('/tire-products/by-group', { params });
      if (response.data?.success && response.data?.data) {
        return { data: response.data.data };
      }
      return { data: Array.isArray(response.data) ? response.data : [] };
    } catch (error) {
      console.error('tiresAPI.getByGroup error:', error);
      throw error;
    }
  }
};

// Battery Brands API
export const batteryBrandsAPI = {
  getAll: async (params) => {
    const response = await api.get('/battery-brands', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/battery-brands', data),
  update: (id, data) => {
    // Laravel doesn't parse FormData text fields with PUT requests
    // Use POST with method spoofing for FormData
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/battery-brands/${id}`, data);
    }
    // For regular JSON data, use PUT
    return api.put(`/battery-brands/${id}`, data);
  },
  delete: (id) => api.delete(`/battery-brands/${id}`),
  show: async (id) => {
    const response = await api.get(`/battery-brands/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Batteries API
export const batteriesAPI = {
  getAll: async (params) => {
    const response = await api.get('/battery-products', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/battery-products', data),
  update: async (id, data) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/battery-products/${id}`, data);
    }
    return api.put(`/battery-products/${id}`, data);
  },
  delete: (id) => api.delete(`/battery-products/${id}`),
  show: async (id) => {
    const response = await api.get(`/battery-products/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Customers API
export const customersAPI = {
  getAll: async (params) => {
    const response = await api.get('/customers', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  show: async (id) => {
    const response = await api.get(`/customers/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Suppliers API
export const suppliersAPI = {
  getAll: async (params) => {
    const response = await api.get('/suppliers', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  show: async (id) => {
    const response = await api.get(`/suppliers/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Locations API
export const locationsAPI = {
  getAll: async (params) => {
    const response = await api.get('/locations', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  show: async (id) => {
    const response = await api.get(`/locations/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Zones API (nested under locations + standalone)
export const zonesAPI = {
  getAll: async (params) => {
    const response = await api.get('/zones', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  getByLocation: async (locationId) => {
    const response = await api.get(`/locations/${locationId}/zones`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  create: (locationId, data) => api.post(`/locations/${locationId}/zones`, data),
  update: (locationId, zoneId, data) => api.put(`/locations/${locationId}/zones/${zoneId}`, data),
  delete: (locationId, zoneId) => api.delete(`/locations/${locationId}/zones/${zoneId}`),
  show: async (locationId, zoneId) => {
    const response = await api.get(`/locations/${locationId}/zones/${zoneId}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Wiper Products API
export const wiperProductsAPI = {
  getAll: async (params) => {
    const response = await api.get('/wiper-products', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/wiper-products', data),
  update: async (id, data) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/wiper-products/${id}`, data);
    }
    return api.put(`/wiper-products/${id}`, data);
  },
  delete: (id) => api.delete(`/wiper-products/${id}`),
  show: async (id) => {
    const response = await api.get(`/wiper-products/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Service Products API
export const servicesProductsAPI = {
  getAll: async (params) => {
    const response = await api.get('/service-products', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/service-products', data),
  update: (id, data) => api.put(`/service-products/${id}`, data),
  delete: (id) => api.delete(`/service-products/${id}`),
  show: async (id) => {
    const response = await api.get(`/service-products/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Made In API
export const madeInsAPI = {
  getAll: async (params) => {
    const response = await api.get('/made-ins', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/made-ins', data),
  update: (id, data) => api.put(`/made-ins/${id}`, data),
  delete: (id) => api.delete(`/made-ins/${id}`),
  show: async (id) => {
    const response = await api.get(`/made-ins/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Products API (Aggregated)
export const productsAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/products', { params });
      const data = extractDataFromResponse(response);
      const inner = response.data?.data; // LengthAwarePaginator wrapper
      const pagination = inner?.current_page ? {
        current_page: inner.current_page,
        last_page:    inner.last_page,
        per_page:     inner.per_page,
        total:        inner.total,
      } : null;
      return { data, pagination };
    } catch (error) {
      console.error('Error fetching products:', error);
      return { data: [], pagination: null };
    }
  },
  create: (data) => {
    switch (data.type) {
      case 'tire':
        return tiresAPI.create(data);
      case 'battery':
        return batteriesAPI.create(data);
      case 'wiper':
        return wiperProductsAPI.create(data);
      case 'service':
        return servicesProductsAPI.create(data);
      default:
        throw new Error('Unknown product type');
    }
  },
  update: (id, data) => {
    switch (data.type) {
      case 'tire':
        return tiresAPI.update(id, data);
      case 'battery':
        return batteriesAPI.update(id, data);
      case 'wiper':
        return wiperProductsAPI.update(id, data);
      case 'service':
        return servicesProductsAPI.update(id, data);
      default:
        throw new Error('Unknown product type');
    }
  },
  delete: (id, type) => {
    switch (type) {
      case 'tire':
        return tiresAPI.delete(id);
      case 'battery':
        return batteriesAPI.delete(id);
      case 'wiper':
        return wiperProductsAPI.delete(id);
      case 'service':
        return servicesProductsAPI.delete(id);
      default:
        throw new Error('Unknown product type');
    }
  },
  show: (id, type) => {
    switch (type) {
      case 'tire':
        return tiresAPI.show(id);
      case 'battery':
        return batteriesAPI.show(id);
      case 'wiper':
        return wiperProductsAPI.show(id);
      case 'service':
        return servicesProductsAPI.show(id);
      default:
        throw new Error('Unknown product type');
    }
  }
};

// Inventory Movements API
export const inventoryMovementsAPI = {
  getAll: async (params) => {
    const response = await api.get('/inventory-movements', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/inventory-movements', data),
  show: async (id) => {
    const response = await api.get(`/inventory-movements/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Stock Adjustments API
export const stockAdjustmentsAPI = {
  getAll: async (params) => {
    const response = await api.get('/stock-adjustments', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/stock-adjustments', data),
  show: async (id) => {
    const response = await api.get(`/stock-adjustments/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Inventories API
export const inventoriesAPI = {
  getAll: async (params) => {
    const response = await api.get('/inventories', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/inventories', data),
  update: (id, data) => api.put(`/inventories/${id}`, data),
  delete: (id) => api.delete(`/inventories/${id}`),
  show: async (id) => {
    const response = await api.get(`/inventories/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  },
  getByProduct: async (type, id) => {
    const response = await api.get(`/inventories/by-product/${type}/${id}`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  getByLocation: async (locationId) => {
    const response = await api.get(`/inventories/by-location/${locationId}`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  adjustInventory: (data) => api.post('/inventories/adjust', data),
  updateQuantity: (id, data) => api.put(`/inventories/${id}/update-quantity`, data),
  getSummary: async (params) => {
    const response = await api.get('/inventories/summary', { params });
    return { ...response, data: extractDataFromResponse(response) };
  },
  moveStock: (data) => api.post('/inventory/move-stock', data)
};

// Invoices API
export const invoicesAPI = {
  getAll: async (params) => {
    const response = await api.get('/invoices', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  show: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  },
  addItem: (invoiceId, data) => api.post(`/invoices/${invoiceId}/items`, data),
  removeItem: (invoiceId, itemId) => api.delete(`/invoices/${invoiceId}/items/${itemId}`),
  updateStatus: (invoiceId, data) => api.put(`/invoices/${invoiceId}/status`, data),
  convertToSale: (invoiceId, data = {}) => api.post(`/invoices/${invoiceId}/convert-to-sale`, data)
};

// Purchases API
export const purchasesAPI = {
  getAll: async (params) => {
    const response = await api.get('/purchases', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
  show: async (id) => {
    const response = await api.get(`/purchases/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  },
  addItem: (purchaseId, data) => api.post(`/purchases/${purchaseId}/items`, data),
  removeItem: (purchaseId, itemId) => api.delete(`/purchases/${purchaseId}/items/${itemId}`),
  updateStatus: (purchaseId, data) => api.put(`/purchases/${purchaseId}/status`, data),
  getReceipts: async (purchaseId) => {
    const response = await api.get(`/purchases/${purchaseId}/receipts`);
    return { ...response, data: extractDataFromResponse(response) };
  }
};

// Sales API
export const salesAPI = {
  getAll: async (params) => {
    const response = await api.get('/sales', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  show: async (id) => {
    const response = await api.get(`/sales/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  },
  addItem: (saleId, data) => api.post(`/sales/${saleId}/items`, data),
  removeItem: (saleId, itemId) => api.delete(`/sales/${saleId}/items/${itemId}`),
  updateStatus: (saleId, data) => api.put(`/sales/${saleId}/status`, data),
  getReceipts: async (saleId) => {
    const response = await api.get(`/sales/${saleId}/receipts`);
    return { ...response, data: extractDataFromResponse(response) };
  }
};

// Receipts API
export const receiptsAPI = {
  getAll: async (params) => {
    const response = await api.get('/receipts', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  create: (data) => api.post('/receipts', data),
  update: (id, data) => api.put(`/receipts/${id}`, data),
  delete: (id) => api.delete(`/receipts/${id}`),
  show: async (id) => {
    const response = await api.get(`/receipts/${id}`);
    return { ...response, data: extractSingleDataFromResponse(response) };
  }
};

// Reports API
export const reportsAPI = {
  salesSummary: async (params) => {
    const response = await api.get('/reports/sales-summary', { params });
    return { ...response, data: extractDataFromResponse(response) };
  },
  inventorySummary: async (params) => {
    const response = await api.get('/inventories/summary', { params });
    return { ...response, data: extractDataFromResponse(response) };
  },
  purchaseSummary: async (params) => {
    const response = await api.get('/reports/purchase-summary', { params });
    return { ...response, data: extractDataFromResponse(response) };
  },
  customerBalance: async (params) => {
    const response = await api.get('/reports/customer-balance', { params });
    return { ...response, data: extractDataFromResponse(response) };
  },
  supplierBalance: async (params) => {
    const response = await api.get('/reports/supplier-balance', { params });
    return { ...response, data: extractDataFromResponse(response) };
  }
};

// Super Admin API
export const superAdminAPI = {
  // Tenant Management
  getAvailableTenants: async () => {
    const response = await api.get('/super-admin/tenants/available');
    return { ...response, data: extractDataFromResponse(response) };
  },
  switchToTenant: (id) => api.post(`/super-admin/tenants/${id}/switch`),
  getCurrentTenantContext: async () => {
    const response = await api.get('/super-admin/tenants/current-context');
    return { ...response, data: extractDataFromResponse(response) };
  },
  clearTenantContext: () => api.delete('/super-admin/tenants/current-context'),
  getAllTenants: async (params) => {
    const response = await api.get('/super-admin/tenants', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  getTenant: async (id) => {
    const response = await api.get(`/super-admin/tenants/${id}`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  createTenant: (data) => api.post('/super-admin/tenants', data),
  updateTenant: (id, data) => api.put(`/super-admin/tenants/${id}`, data),
  deleteTenant: (id) => api.delete(`/super-admin/tenants/${id}`),
  suspendTenant: (id, data) => api.post(`/super-admin/tenants/${id}/suspend`, data),
  activateTenant: (id) => api.post(`/super-admin/tenants/${id}/activate`),
  
  // Tenant Data Management
  getTenantData: async (tenantId, dataType) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/data/${dataType}`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  getTenantUsers: async (tenantId) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/users`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  getTenantAnalytics: async (tenantId, period) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/analytics`, { params: { period } });
    return { ...response, data: extractDataFromResponse(response) };
  },
  backupTenantData: (tenantId) => api.post(`/super-admin/tenants/${tenantId}/backup`),
  restoreTenantData: (tenantId, backupId) => api.post(`/super-admin/tenants/${tenantId}/restore`, { backup_id: backupId }),
  exportTenantData: async (tenantId, format) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/export`, {
      params: { format },
      responseType: format === 'csv' || format === 'excel' ? 'blob' : 'json'
    });
    return response;
  },
  
  // Tenant Billing
  getTenantBilling: async (tenantId) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/billing`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  updateTenantBilling: (tenantId, data) => api.put(`/super-admin/tenants/${tenantId}/billing`, data),
  
  // System Management
  getSystemBilling: async () => {
    const response = await api.get('/super-admin/billing');
    return { ...response, data: extractDataFromResponse(response) };
  },
  getSystemStats: async () => {
    const response = await api.get('/super-admin/system/stats');
    return { ...response, data: extractDataFromResponse(response) };
  },
  getSystemSettings: async () => {
    const response = await api.get('/super-admin/system/settings');
    return { ...response, data: extractDataFromResponse(response) };
  },
  updateSystemSettings: (data) => api.put('/super-admin/system/settings', data),
  getSystemLogs: async (params) => {
    const response = await api.get('/super-admin/system/logs', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  getSystemHealth: async () => {
    const response = await api.get('/super-admin/system/health');
    return { ...response, data: extractDataFromResponse(response) };
  },
  
  // Alerts Management
  getTenantAlerts: async (tenantId) => {
    const response = await api.get(`/super-admin/tenants/${tenantId}/alerts`);
    return { ...response, data: extractDataFromResponse(response) };
  },
  getSystemAlerts: async () => {
    const response = await api.get('/super-admin/system/alerts');
    return { ...response, data: extractDataFromResponse(response) };
  },
  
  // User Management
  getAllUsers: async (params) => {
    const response = await api.get('/super-admin/users', { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  },
  createUser: (data) => api.post('/super-admin/users', data),
  updateUser: (id, data) => api.put(`/super-admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/super-admin/users/${id}`),
  assignUserToTenant: (userId, tenantId) => api.post(`/super-admin/users/${userId}/assign-tenant`, { tenant_id: tenantId }),
  removeUserFromTenant: (userId) => api.delete(`/super-admin/users/${userId}/assign-tenant`)
};

// Product Counts API (sidebar)
export const productCountsAPI = {
  getCounts: async () => {
    const response = await api.get('/product-counts');
    return extractDataFromResponse(response);
  }
};

// Product History API
export const productHistoryAPI = {
  getByProduct: async (type, id, params) => {
    const response = await api.get(`/products/${type}/${id}/history`, { params });
    const data = extractDataFromResponse(response);
    const pagination = extractPaginationInfo(response);
    return { ...response, data, pagination };
  }
};

// Product Analytics API
export const productAnalyticsAPI = {
  getByProduct: async (type, id) => {
    const response = await api.get(`/products/${type}/${id}/analytics`);
    return extractDataFromResponse(response);
  }
};

// Product Price API
export const productPriceAPI = {
  updatePrice: (type, id, data) => api.put(`/products/${type}/${id}/price`, data)
};

// Stock Aging API
export const stockAgingAPI = {
  getByProduct: async (type, id) => {
    const response = await api.get(`/products/${type}/${id}/stock-aging`);
    return extractDataFromResponse(response);
  },
  getByType: async (type) => {
    const response = await api.get(`/stock-aging/${type}`);
    return extractDataFromResponse(response);
  },
};

// Dashboard Analytics API
export const dashboardAnalyticsAPI = {
  getFlow: async (days = 30) => {
    const response = await api.get('/dashboard/analytics/flow', { params: { days } });
    return extractDataFromResponse(response);
  },
  getDistribution: async () => {
    const response = await api.get('/dashboard/analytics/distribution');
    return extractDataFromResponse(response);
  },
  getAging: async () => {
    const response = await api.get('/dashboard/analytics/aging');
    return extractDataFromResponse(response);
  },
  getMargins: async () => {
    const response = await api.get('/dashboard/analytics/margins');
    return extractDataFromResponse(response);
  },
  getAlerts: async () => {
    const response = await api.get('/dashboard/analytics/alerts');
    return extractDataFromResponse(response);
  },
  getRecentMovements: async () => {
    const response = await api.get('/dashboard/analytics/recent-movements');
    return extractDataFromResponse(response);
  },
};

// Export default axios instance
export default api;